/**
 * Reference-kind FieldTypes.
 *
 * Storage strategy: a row id (or array of ids) is stored, pointing at the
 * `table` named on the FieldType, and that row is what renders. The id may live
 * in a fixed top-level column (`record.parent_id`, `record.owner_id`) or in the
 * jsonb data blob at `record.data[key]` for schema-defined fields.
 *
 * Junctions (link/tagging/attachment) are references too — they own the row in
 * `table` and carry an inner `reference` to the real entity — and live in
 * `./junction.ts`, spreading the entries here. Data only: operator groups live
 * in `../operators/reference.ts`.
 */

import * as group from "../group.js"
import * as sort from "../sort.js"
import type { ReferenceFieldType } from "../types.js"
import { linkableTypeNames } from "./linkableTypeNames.js"

// Reference subtypes that resolve against their own tables (users/schemas/types)
type SystemReferenceType = "record" | "schema" | "type" | "user"
// Reference subtypes backed by record_-prefixed tables
type RecordReferenceType = "contact" | "source" | "time"
// References with their own tables that also serve as a junction's inner `reference`
type JunctionReferenceType = "asset" | "tag"

// Reference-kind type names: top-level references (the resolved row is the
// rendered thing) plus subtypes named for their immediate parent. `record`/`tag`/
// `asset` double as the target kinds a junction's inner `reference` names.
export type ReferenceType = SystemReferenceType | RecordReferenceType | JunctionReferenceType

// Subtypes of reference types
export type TimeType = "instant" | "event" | "hours" | "slot" | "lifetime"
export type ContactType = "email" | "phone" | "url" | "location" | "socials"
export type RecordType = "parent" | "related"

interface ContactRow {
  value?: string
  label?: string
}

interface TimeRow {
  start_at?: string
  end_at?: string
  title?: string
}

interface SourceRow {
  source_type?: string
  source_id?: string
  label?: string
}

interface UserRow {
  name?: string
  email?: string
}

// Social-media platforms a contact's `label` can identify, with the icon to
// render and the label for the picker. Contact-type knowledge, so it lives with
// the contact FieldType. Consumers match a contact row's `label` against `key`.
export const SOCIAL_PLATFORMS = [
  { key: "website", label: "Website", icon: ["fas", "square-share-nodes"] },
  { key: "linkedin", label: "LinkedIn", icon: ["fab", "linkedin"] },
  { key: "github", label: "GitHub", icon: ["fab", "github-square"] },
  { key: "twitter", label: "Twitter/X", icon: ["fab", "twitter-square"] },
  { key: "mastodon", label: "Mastodon", icon: ["fab", "mastodon"] },
  { key: "ycombinator", label: "HackerNews", icon: ["fab", "hacker-news-square"] },
  { key: "bsky", label: "Bluesky", icon: ["fab", "square-bluesky"] },
  { key: "facebook", label: "Facebook", icon: ["fab", "facebook-square"] },
  { key: "whatsapp", label: "WhatsApp", icon: ["fab", "whatsapp-square"] },
]

const SOCIAL_PLATFORM_KEYS = new Set(SOCIAL_PLATFORMS.map((p) => p.key))

// The hosts each platform's links live on (a subdomain counts). Mastodon has no
// one host, so its links read as a website.
const SOCIAL_HOSTS: Record<string, string> = {
  "linkedin.com": "linkedin",
  "github.com": "github",
  "twitter.com": "twitter",
  "x.com": "twitter",
  "news.ycombinator.com": "ycombinator",
  "bsky.app": "bsky",
  "facebook.com": "facebook",
  "wa.me": "whatsapp",
  "whatsapp.com": "whatsapp",
}

/** The `SOCIAL_PLATFORMS` key a link belongs to, by its host — what labels a new
 *  socials row. Anything else is the record's website. */
export function socialPlatformOf(url: string): string {
  let host: string
  try {
    host = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase()
  } catch {
    return "website"
  }
  for (const [domain, platform] of Object.entries(SOCIAL_HOSTS)) {
    if (host === domain || host.endsWith(`.${domain}`)) return platform
  }
  return "website"
}

/** Whether a contact row is a social-media one — its `label` names a
 *  `SOCIAL_PLATFORMS` platform. The single predicate behind the `socials` field's
 *  read-time filter and the socials chip rendering. */
export const isSocialContact = (contact: { label?: string | null } | null | undefined): boolean =>
  !!contact?.label && SOCIAL_PLATFORM_KEYS.has(contact.label.toLowerCase())

/** REFERENCE (root) — fields storing an id (or ids) that resolve into a table.
 *  `table` is `records` (the abstract root is never a stored field type).
 *  Standalone so the top-level references can spread it. */
export const REFERENCE: ReferenceFieldType = {
  name: "reference",
  kind: "reference",
  label: "Reference",
  icon: "arrow-up-right-from-square",
  table: "records",
}

// Top-level references — each spreads `REFERENCE` and declares its `table`;
// subtypes are added below, spreading their parent entry. The literal key union
// forces every top-level reference to be present and makes each a known property
// the subtype spreads read.
export const fieldTypes: Record<"reference" | ReferenceType, ReferenceFieldType> = {
  reference: REFERENCE,

  // SCHEMA — stored id resolves to a `schemas` row.
  schema: {
    ...REFERENCE,
    name: "schema",
    label: "Schema",
    parent: "reference",
    icon: "shapes",
    table: "schemas",
    sort: sort.text,
    ...group.byTarget,
    format: (value: unknown) => (value == null ? "" : String(value)),
    parse: (input: unknown) => (input == null || input === "" ? null : Number(input) || null),
  },

  // TIME + subtypes (below). The stored id resolves against the record's `times`
  // relation. Parent `format` renders the common single start_at; `event`
  // overrides for a start→end range.
  time: {
    ...REFERENCE,
    name: "time",
    label: "Time",
    parent: "reference",
    icon: "clock",
    table: "record_times",
    relationship: "times",
    sort: sort.occurrence,
    format: (row: unknown) => {
      if (row == null) return ""
      if (typeof row !== "object") return String(row)
      const r = row as TimeRow
      return r.start_at || r.title || ""
    },
    parse: (input: unknown) => (input == null || input === "" ? null : Number(input) || input),
  },

  // CONTACT + subtypes (below). Parent reads `value` off the contact row; the
  // stored id resolves against the record's `contacts` relation.
  contact: {
    ...REFERENCE,
    name: "contact",
    label: "Contact",
    parent: "reference",
    icon: "address-book",
    table: "record_contacts",
    relationship: "contacts",
    format: (row: unknown) => {
      if (row == null) return ""
      if (typeof row !== "object") return String(row)
      const r = row as ContactRow
      return r.value || r.label || ""
    },
    parse: (input: unknown) => (input == null || input === "" ? null : Number(input) || input),
  },

  // USER — stored id resolves to a `users` row.
  user: {
    ...REFERENCE,
    name: "user",
    label: "User",
    parent: "reference",
    icon: "user",
    table: "users",
    sort: sort.text,
    ...group.byTarget,
    format: (row: unknown) => {
      if (row == null) return ""
      if (typeof row !== "object") return String(row)
      const r = row as UserRow
      return r.name || r.email || ""
    },
    parse: (input: unknown) => (input == null || input === "" ? null : input),
  },

  // SOURCE — the stored id resolves against the record's `sources` relation.
  source: {
    ...REFERENCE,
    name: "source",
    label: "Source",
    parent: "reference",
    icon: "plug",
    table: "record_sources",
    relationship: "sources",
    ...group.byTarget,
    format: (row: unknown) => {
      if (row == null) return ""
      if (typeof row !== "object") return String(row)
      const r = row as SourceRow
      return r.source_type || r.label || ""
    },
    parse: (input: unknown) => (input == null || input === "" ? null : Number(input) || input),
  },

  // RECORD — a direct FK from `records` (e.g. parent_id) into `records`. Dynamic
  // subtypes scope the field to a record type (a parent that must be an
  // organization), mirroring `link`; unset scopes to any record type. Generic
  // `record` has no single canonical item relation, so it declares no
  // `relationship` and stays raw-id; the `parent` subtype (below) is resolvable.
  record: {
    ...REFERENCE,
    name: "record",
    label: "Record",
    parent: "reference",
    icon: "file-alt",
    table: "records",
    sort: sort.text,
    ...group.byTarget,
    recordTypes: () => linkableTypeNames(),
  },

  // TAG — a direct reference to a `tags` row (distinct from `tagging`, the
  // junction that owns a record_taggings row). The kind `tagging`'s inner
  // reference names.
  tag: {
    ...REFERENCE,
    name: "tag",
    label: "Tag",
    parent: "reference",
    icon: "sitemap",
    table: "tags",
    sort: sort.text,
    ...group.byTarget,
  },

  // ASSET — a direct reference to an `assets` row (distinct from `attachment`,
  // the junction that owns a record_attachments row). The kind `attachment`'s
  // inner reference names.
  asset: {
    ...REFERENCE,
    name: "asset",
    label: "Asset",
    parent: "reference",
    table: "assets",
  },

  // TYPE — `records.type` is a string FK into `types.name`. Stores a string
  // rather than an integer id, but the lookup pattern is the same. `recordTypes`
  // exposes the live entity-type registry for scoping.
  type: {
    ...REFERENCE,
    name: "type",
    label: "Type",
    parent: "reference",
    icon: "shapes",
    table: "types",
    sort: sort.text,
    ...group.byTarget,
    recordTypes: () => linkableTypeNames(),
  },
}

// TIME subtypes — spread `fieldTypes.time`. instant/hours/slot/lifetime are
// structural placeholders; `event` overrides format for start→end ranges.
export const timeTypes: Record<TimeType, ReferenceFieldType> = {
  instant: { ...fieldTypes.time, name: "instant", label: "Instant", parent: "time" },
  event: {
    ...fieldTypes.time,
    name: "event",
    label: "Event",
    parent: "time",
    icon: "calendar-days",
    format: (row: unknown) => {
      if (row == null) return ""
      if (typeof row !== "object") return String(row)
      const r = row as TimeRow
      if (r.start_at && r.end_at) return `${r.start_at} → ${r.end_at}`
      return r.start_at || r.title || ""
    },
  },
  hours: { ...fieldTypes.time, name: "hours", label: "Hours", parent: "time" },
  slot: { ...fieldTypes.time, name: "slot", label: "Slot", parent: "time" },
  lifetime: { ...fieldTypes.time, name: "lifetime", label: "Lifetime", parent: "time" },
}

// Contact normalizers — parsing IS validation: each trims/normalizes the typed
// value and returns it, or `null` when malformed.
const parseEmail = (input: unknown): string | null => {
  const v = (input == null ? "" : String(input)).trim().toLowerCase()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : null
}
// Preserve the number as typed (dashes, spaces, parens, "+", "." are all valid
// phone punctuation) — nothing dedupes phones, so readability wins. Trim and
// require enough digits to be plausible.
const parsePhone = (input: unknown): string | null => {
  const v = (input == null ? "" : String(input)).trim()
  return v.replace(/\D/g, "").length >= 4 ? v : null
}
const parseUrl = (input: unknown): string | null => {
  const v = (input == null ? "" : String(input)).trim()
  return /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,6}(\/[^\s]*)?$/i.test(v) ? v : null
}
const parseAddress = (input: unknown): string | null => {
  const v = (input == null ? "" : String(input)).trim()
  return v.length > 0 ? v : null
}

// CONTACT subtypes — spread `fieldTypes.contact`, override `parse` with the
// per-type normalizer. socials is the social-media subset of a record's contacts
// (derived at read time), inheriting the relation + format but registering its
// own icon-only cell.
export const contactTypes: Record<ContactType, ReferenceFieldType> = {
  email: {
    ...fieldTypes.contact,
    name: "email",
    label: "Email",
    parent: "contact",
    icon: "envelope",
    parse: parseEmail,
  },
  phone: {
    ...fieldTypes.contact,
    name: "phone",
    label: "Phone",
    parent: "contact",
    icon: "phone",
    parse: parsePhone,
  },
  location: {
    ...fieldTypes.contact,
    name: "location",
    label: "Location",
    parent: "contact",
    icon: "map-marker-alt",
    parse: parseAddress,
  },
  url: {
    ...fieldTypes.contact,
    name: "url",
    label: "URL",
    parent: "contact",
    icon: "globe",
    parse: parseUrl,
  },
  socials: {
    ...fieldTypes.contact,
    name: "socials",
    label: "Socials",
    parent: "contact",
    icon: "share-nodes",
    parse: parseUrl,
  },
}

// RECORD subtypes — `parent` is the fixed `parent_id` column, resolving via the
// record's `parent` relation. `related` is records reached through a computed
// relation on the record (`events` / `organizations` / `people`: the records joined
// to it through a list, either way — the owner of a list it's listed in, or a record
// listed in a list it owns) — a direct field, its whole relation the value. The field's
// key names the relation, so the def declares no `relationship` (each field reads a
// different one); and being computed, it has no rows to write: read-only.
export const recordTypes: Record<RecordType, ReferenceFieldType> = {
  parent: {
    ...fieldTypes.record,
    name: "parent",
    label: "Parent",
    parent: "record",
    relationship: "parent",
  },
  related: {
    ...fieldTypes.record,
    name: "related",
    label: "Related",
    parent: "record",
    icon: "diagram-project",
    isEditable: false,
  },
}

Object.assign(fieldTypes, timeTypes, contactTypes, recordTypes)
